/**
 * Minimal, dependency-free text splitter.
 *
 * GSAP's SplitText is an extra surface area we don't need here, and a naive
 * splitter breaks on resize. This one:
 *   - wraps words/chars for `words` and `chars` modes;
 *   - for `lines`, wraps words first, then groups them into visual lines by
 *     comparing offsetTop, which is the only reliable cross-browser way;
 *   - keeps the original text available to assistive tech via aria-label, so a
 *     screen reader never reads a heading one letter at a time.
 *
 * Returns an array of the outermost animatable elements (lines / words / chars).
 */

export function splitText(el, mode = 'lines') {
  if (!el || el.dataset.splitDone === mode) {
    return Array.from(el ? el.querySelectorAll('.split-line, .split-word, .split-char') : []);
  }

  const original = el.textContent.replace(/\s+/g, ' ').trim();
  if (original) {
    el.setAttribute('aria-label', original);
  }

  // Preserve inline markup? Not needed for our headlines, which are plain text
  // or a single <em>. We keep <em> by walking child nodes instead of using
  // textContent, so italics survive.
  const frag = document.createDocumentFragment();

  const pushTokens = (node, inheritedTag) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const parts = child.textContent.split(/(\s+)/);
        for (const part of parts) {
          if (part === '') continue;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(' '));
          } else {
            const word = document.createElement('span');
            word.className = 'split-word';
            word.style.display = 'inline-block';
            word.textContent = part;
            if (inheritedTag) {
              const w = document.createElement(inheritedTag);
              w.appendChild(word);
              frag.appendChild(w);
            } else {
              frag.appendChild(word);
            }
          }
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        pushTokens(child, child.tagName.toLowerCase());
      }
    }
  };

  pushTokens(el, null);

  el.textContent = '';
  el.appendChild(frag);

  const words = Array.from(el.querySelectorAll('.split-word'));

  if (mode === 'words') {
    return words;
  }

  if (mode === 'chars') {
    return words;
  }

  // ---- tokenise what pass 1 produced, preserving the exact order of words and
  //      whitespace. The separators are sibling text nodes, not part of the
  //      word spans, so they must be carried along explicitly. A rebuild that
  //      moves only the word spans leaves every separator behind and
  //      concatenates the headline ("Fourdecadesof findingsolutions...").
  const tokens = [];

  Array.from(el.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (/\s/.test(node.textContent)) tokens.push({ kind: 'space', node });
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const word = node.classList.contains('split-word')
      ? node
      : node.querySelector('.split-word');

    if (word) tokens.push({ kind: 'word', node, word });
  });

  // ---- assign each token to a visual line using the word's rendered top
  //      offset. Whitespace inherits the line of the word that precedes it.
  let lineIndex = -1;
  let lastTop = null;

  tokens.forEach((t) => {
    if (t.kind === 'space') {
      t.line = lineIndex;
      return;
    }

    const top = Math.round(t.word.offsetTop);
    if (lastTop === null || top !== lastTop) {
      lineIndex += 1;
      lastTop = top;
    }

    t.line = lineIndex;
  });

  // ---- pass 2: rebuild as masked lines, replaying the ORIGINAL token
  //      sequence. Manufacturing separators here would put a space in front of
  //      a following full stop ("willingable .").
  const rebuilt = document.createDocumentFragment();
  const out = [];

  for (let i = 0; i <= lineIndex; i += 1) {
    const lineTokens = tokens.filter((t) => t.line === i);

    // Whitespace that wrapped onto the next line is only the line break.
    while (lineTokens.length && lineTokens[0].kind === 'space') lineTokens.shift();
    while (lineTokens.length && lineTokens[lineTokens.length - 1].kind === 'space') lineTokens.pop();

    if (!lineTokens.length) continue;

    const mask = document.createElement('span');
    mask.className = 'split-mask';
    mask.style.display = 'block';

    const line = document.createElement('span');
    line.className = 'split-line';
    line.style.display = 'block';

    lineTokens.forEach((t) => {
      if (t.kind === 'space') {
        line.appendChild(document.createTextNode(' '));
        return;
      }

      // Move the whole node. For an <em> that is the wrapper — so italic
      // styling survives — and for a bare word it is the span itself.
      line.appendChild(t.node);
    });

    mask.appendChild(line);
    rebuilt.appendChild(mask);
    out.push(line);
  }

  el.textContent = '';
  el.appendChild(rebuilt);
  el.dataset.splitDone = mode;

  return out;
}

/**
 * Split a string into per-character spans (used for the WILLINGABLE word,
 * which is authored as individual spans in PHP and only needs collecting).
 */
export function collectChars(el) {
  return Array.from(el.querySelectorAll('.ch'));
}
